import type { MaybeRefOrGetter } from 'vue'
import type { ContentTocItem } from '~/utils/contentToc'
import { collectContentTocItems } from '~/utils/contentToc'

type ContentTocOptions = {
  enabled: MaybeRefOrGetter<boolean>
}

export const useContentToc = (options: ContentTocOptions) => {
  const enabled = computed(() => toValue(options.enabled))
  const tocItems = ref<ContentTocItem[]>([])
  const tocBoxRef = ref<HTMLElement | null>(null)
  const tocCollapsed = ref(false)
  const { items: sidebarTocItems, floating: sidebarTocFloating, activeId: sidebarTocActiveId } = useContentTocState()
  let tocObserver: IntersectionObserver | null = null
  let tocActiveFrame = 0

  const hasToc = computed(() => enabled.value && tocItems.value.length > 0)

  const clearSidebarToc = () => {
    sidebarTocItems.value = []
    sidebarTocFloating.value = false
    sidebarTocActiveId.value = ''
  }

  const syncSidebarTocItems = () => {
    sidebarTocItems.value = hasToc.value ? tocItems.value : []
    if (!sidebarTocItems.value.length) {
      sidebarTocFloating.value = false
      sidebarTocActiveId.value = ''
    }
  }

  const disconnectTocObserver = () => {
    tocObserver?.disconnect()
    tocObserver = null
  }

  const bindTocObserver = () => {
    if (!import.meta.client) {
      return
    }
    disconnectTocObserver()
    sidebarTocFloating.value = false
    const target = tocBoxRef.value
    if (!target || !hasToc.value) {
      return
    }
    tocObserver = new IntersectionObserver(entries => {
      const entry = entries[0]
      const anchorOffset = getAnchorOffset()
      sidebarTocFloating.value = hasToc.value && !!entry && !entry.isIntersecting && entry.boundingClientRect.bottom <= anchorOffset
    }, { rootMargin: `-${getAnchorOffset()}px 0px 0px 0px` })
    tocObserver.observe(target)
  }

  const syncActiveTocId = () => {
    if (!import.meta.client || !hasToc.value) {
      sidebarTocActiveId.value = ''
      return
    }
    const anchorOffset = getAnchorOffset()
    let current = tocItems.value[0]?.id || ''
    for (const item of tocItems.value) {
      const node = document.getElementById(item.id)
      if (!node) {
        continue
      }
      if (node.getBoundingClientRect().top <= anchorOffset + 8) {
        current = item.id
        continue
      }
      break
    }
    sidebarTocActiveId.value = current
  }

  const requestActiveTocSync = () => {
    if (!import.meta.client || tocActiveFrame) {
      return
    }
    tocActiveFrame = window.requestAnimationFrame(() => {
      tocActiveFrame = 0
      syncActiveTocId()
    })
  }

  const onTocChange = (items: ContentTocItem[]) => {
    tocItems.value = enabled.value ? items : []
    syncSidebarTocItems()
    nextTick(() => {
      bindTocObserver()
      syncActiveTocId()
    })
  }

  const refreshFromElement = (root: HTMLElement | null) => {
    if (!enabled.value || !root) {
      onTocChange([])
      return
    }
    onTocChange(collectContentTocItems(root))
  }

  const toggleToc = () => {
    tocCollapsed.value = !tocCollapsed.value
  }

  watch(enabled, value => {
    if (!value) {
      tocItems.value = []
      disconnectTocObserver()
      clearSidebarToc()
    }
  })

  onMounted(() => {
    syncSidebarTocItems()
    nextTick(() => {
      bindTocObserver()
      syncActiveTocId()
    })
    window.addEventListener('scroll', requestActiveTocSync, { passive: true })
  })

  onBeforeUnmount(() => {
    disconnectTocObserver()
    clearSidebarToc()
    window.removeEventListener('scroll', requestActiveTocSync)
    if (tocActiveFrame) {
      window.cancelAnimationFrame(tocActiveFrame)
      tocActiveFrame = 0
    }
  })

  return {
    tocItems,
    tocBoxRef,
    tocCollapsed,
    hasToc,
    onTocChange,
    refreshFromElement,
    toggleToc
  }
}
